"use client";
import Icon from "@/components/atoms/Icons";
import Image from "@/components/atoms/image";
import { ReactNode } from "react";
import { Icon as AmaIcon } from "@ama-pt/agora-design-system";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import Heading from "@/components/molecules/Heading";
import { ImageData } from "@/page-builder/fragments/StatsCardsBlock/types";
import { Link } from "@/i18n/navigation";

export interface AchievedGridProps {
  title: ReactNode;
  items: {
    text: string;
    index: number;
    percentage: boolean;
    image: ImageData;
  }[];
  linkBtn: {
    label: string;
    url: string;
    target: string;
  };
}

const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.2,
      ease: "easeOut",
    },
  }),
};

export const AchievedGrid = ({
  items = [],
  title = "",
  linkBtn,
}: AchievedGridProps) => {
  const parseIndex = (idx: number | undefined): number => {
    if (typeof idx !== "number") return 0;
    return idx;
  };

  return (
    <div className="container mx-auto">
      <div className="grid grid-cols-6 gap-[32px]">
        <div className="col-span-6 xl:md:col-span-2">
          <Heading title={title} />
        </div>

        <div className="col-span-6 md:col-span-3 xl:col-span-2 grid grid-rows-2 gap-[24px] overflow-hidden ">
          {items[0]?.index && (
            <motion.div
              variants={fadeUpVariants as any}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0}
              className="relative flex w-full xl:min-h-[360px]
                       md:min-h-[300px] min-h-[276px]"
            >
              <div className="bg-[#FEF8F6] flex flex-col w-full">
                <div className=" pt-[44px] pb-[44px] pl-[32px] pr-[32px] xl:pt-[44px] xl:pb-[44px] xl:pl-[44px] xl:pr-[44px]  gap-[8px] flex flex-col justify-end flex-1">
                  <span className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]">
                    <CountUp
                      start={0}
                      end={parseIndex(items[0]?.index) ?? 0}
                      duration={0.8}
                      separator=" "
                      enableScrollSpy
                      scrollSpyOnce
                      delay={0 * 0.2}
                      className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]"
                    >
                      {({ countUpRef }) => (
                        <span
                          className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]"
                          ref={countUpRef}
                        />
                      )}
                    </CountUp>
                    {items[0]?.percentage && "%"}
                  </span>
                  <span className="text-neutral-900 text-m-regular leading-[28px]">
                    {items[0]?.text}
                  </span>
                </div>
                <div className="absolute top-0 right-0 w-[150px] h-[146px] flex items-center justify-center">
                  <Icon name="fig7" />
                </div>
                <div className="absolute w-[72px] h-[72px] top-[30px] right-[26px]">
                  {items[0]?.image?.url && (
                    <Image
                      fill
                      sizes="60px"
                      alt={""}
                      src={`${items[0]?.image?.url}`}
                    />
                  )}
                </div>
                <div className="absolute bottom-0 left-0 w-[153px] h-[43px]">
                  <Icon name="fig8" />
                </div>
              </div>
            </motion.div>
          )}
          {items[1]?.index && (
            <motion.div
              variants={fadeUpVariants as any}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={1}
              className="md:hidden relative flex w-full min-h-[400px]"
            >
              <div className="bg-[#CFE5E0] flex flex-col w-full">
                <div
                  className="flex flex-col justify-end flex-1
                              pt-[44px] pb-[44px] pl-[32px] pr-[32px]
                              xl:pt-[44px] xl:pb-[44px] xl:pl-[44px] xl:pr-[44px]
                              gap-[8px]"
                >
                  <span className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem]  leading-[60px] xl:leading-[80px]">
                    <CountUp
                      start={0}
                      end={parseIndex(items[1]?.index) ?? 0}
                      duration={0.8}
                      separator=" "
                      enableScrollSpy
                      delay={1 * 0.2}
                      scrollSpyOnce
                      className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem]  leading-[60px] xl:leading-[80px]"
                    >
                      {({ countUpRef }) => (
                        <span
                          className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]"
                          ref={countUpRef}
                        />
                      )}
                    </CountUp>
                    {items[1]?.percentage && "%"}
                  </span>
                  <span className="text-neutral-900 text-m-regular leading-[28px]">
                    {items[1]?.text}
                  </span>
                </div>
                <div className="absolute top-0 right-0 w-[150px] h-[146px] flex items-center justify-center">
                  <Icon name="fig7" color="#0E8668" />
                </div>
                <div className="absolute w-[72px] h-[72px] top-[30px] right-[26px]">
                  {items[1]?.image?.url && (
                    <Image
                      fill
                      sizes="60px"
                      alt={""}
                      src={`${items[1]?.image?.url}`}
                    />
                  )}
                </div>
                <div className="absolute bottom-0 left-[calc(50%-80px)] w-[206px] h-[39px]">
                  <Icon name="fig9" />
                </div>
              </div>
            </motion.div>
          )}
          {items[2]?.index && (
            <motion.div
              variants={fadeUpVariants as any}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={2}
              className="relative flex w-full xl:min-h-[360px]
                       md:min-h-[300px] min-h-[276px]"
            >
              <div className="bg-[#BFD9C8] flex flex-col w-full overflow-hidden relative">
                <div
                  className="flex flex-col justify-end flex-1
                              pt-[44px] pb-[44px] pl-[32px] pr-[32px]
                              xl:pt-[44px] xl:pb-[44px] xl:pl-[44px] xl:pr-[44px]
                              gap-[8px]"
                >
                  <span className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]">
                    <CountUp
                      start={0}
                      end={parseIndex(items[2]?.index) ?? 0}
                      delay={2 * 0.2}
                      duration={0.8}
                      separator=" "
                      enableScrollSpy
                      className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]"
                      scrollSpyOnce
                    >
                      {({ countUpRef }) => (
                        <span
                          className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]"
                          ref={countUpRef}
                        />
                      )}
                    </CountUp>
                    {items[2]?.percentage && "%"}
                  </span>
                  <span className="text-neutral-900 text-m-regular leading-[28px]">
                    {items[2]?.text}
                  </span>
                </div>
                <div className="absolute top-0 right-0 w-[150px] h-[146px] flex items-center justify-center">
                  <Icon name="fig7" color="#0C735A" />
                </div>
                <div className="absolute w-[72px] h-[72px] top-[30px] right-[26px]">
                  {items[2]?.image?.url && (
                    <Image
                      fill
                      sizes="60px"
                      alt={""}
                      src={`${items[2]?.image?.url}`}
                    />
                  )}
                </div>
                <div className="absolute bottom-0 left-0 w-[153px] h-[33px]">
                  <Icon name="fig8" color="#CFE5E0" />
                </div>
              </div>
            </motion.div>
          )}
          {items[3]?.index && (
            <motion.div
              variants={fadeUpVariants as any}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={3}
              className="md:hidden relative flex w-full min-h-[400px] overflow-hidden"
            >
              <div className="bg-[#FDEEE8] flex flex-col w-full overflow-hidden">
                <div
                  className="flex flex-col justify-end flex-1
                              pt-[44px] pb-[44px] pl-[32px] pr-[32px]
                              xl:pt-[44px] xl:pb-[44px] xl:pl-[44px] xl:pr-[44px]
                              gap-[8px]"
                >
                  <span className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]">
                    <CountUp
                      start={0}
                      end={parseIndex(items[3]?.index) ?? 0}
                      delay={3 * 0.2}
                      duration={0.8}
                      separator=" "
                      enableScrollSpy
                      scrollSpyOnce
                      className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]"
                    >
                      {({ countUpRef }) => (
                        <span
                          className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]"
                          ref={countUpRef}
                        />
                      )}
                    </CountUp>
                    {items[3]?.percentage && "%"}
                  </span>
                  <span className="text-neutral-900 text-m-regular leading-[28px]">
                    {items[3]?.text}
                  </span>
                </div>
                <div className="absolute top-0 right-0 w-[150px] h-[146px] flex items-center justify-center">
                  <Icon name="fig7" color="#F6B89B" />
                </div>
                <div className="absolute w-[72px] h-[72px] top-[30px] right-[26px]">
                  {items[3]?.image?.url && (
                    <Image
                      fill
                      sizes="60px"
                      alt={""}
                      src={`${items[3]?.image?.url}`}
                    />
                  )}
                </div>
                <div className="absolute bottom-0 left-[calc(50%-80px)] w-[206px] h-[39px]">
                  <Icon name="fig9" color="#FADACC" />
                </div>
              </div>
            </motion.div>
          )}
          {items[4]?.index && (
            <motion.div
              variants={fadeUpVariants as any}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={4}
              className="relative flex w-full xl:min-h-[360px]
                       md:min-h-[300px] min-h-[276px] overflow-hidden"
            >
              <div className="bg-[#FADACC] flex flex-col w-full overflow-hidden relative">
                <div
                  className="flex flex-col justify-end flex-1
                              pt-[44px] pb-[44px] pl-[32px] pr-[32px]
                              xl:pt-[44px] xl:pb-[44px] xl:pl-[44px] xl:pr-[44px]
                              gap-[8px]"
                >
                  <span className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]">
                    <CountUp
                      start={0}
                      end={parseIndex(items[4]?.index) ?? 0}
                      delay={4 * 0.2}
                      duration={0.8}
                      separator=" "
                      enableScrollSpy
                      scrollSpyOnce
                      className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]"
                    >
                      {({ countUpRef }) => (
                        <span
                          className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]"
                          ref={countUpRef}
                        />
                      )}
                    </CountUp>
                  </span>
                  {items[4]?.percentage && "%"}
                  <span className="text-neutral-900 text-m-regular leading-[28px]">
                    {items[4]?.text}
                  </span>
                </div>
                <div className="absolute top-0 right-0 w-[150px] h-[146px] flex items-center justify-center">
                  <Icon name="fig7" />
                </div>
                <div className="absolute w-[72px] h-[72px] top-[30px] right-[26px]">
                  {items[0]?.image?.url && (
                    <Image
                      fill
                      sizes="60px"
                      alt={""}
                      src={`${items[4]?.image?.url}`}
                    />
                  )}
                </div>
                <div className="absolute bottom-0 left-0 w-[153px] h-[33px]">
                  <Icon name="fig8" color="#FEF8F6" />
                </div>
              </div>
            </motion.div>
          )}

          <Link
            target={linkBtn.target ?? "_self"}
            href={linkBtn.url as any ?? "#"}
            className="agora-anchor-link-primary text-m-semibold py-16 flex
                       items-center justify-between gap-8 md:justify-start text-primary-600"
          >
            {linkBtn.label}
            <AmaIcon
              name="agora-line-arrow-right-circle"
              className="w-6 h-6"
              aria-hidden="true"
            />
          </Link>
        </div>

        <div className="hidden md:grid col-span-6 md:col-span-3 xl:col-span-2 gap-[32px]  self-start">
          {items[1]?.index && (
            <motion.div
              variants={fadeUpVariants as any}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={1}
            >
              <div className="bg-[#CFE5E0] min-h-[400px] xl:min-h-[460px] flex flex-col overflow-hidden relative">
                <div
                  className="flex flex-col justify-end flex-1
                              pt-[44px] pb-[44px] pl-[32px] pr-[32px]
                              xl:pt-[44px] xl:pb-[44px] xl:pl-[44px] xl:pr-[44px]
                              gap-[8px]"
                >
                  <span className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem]  leading-[60px] xl:leading-[80px]">
                    <CountUp
                      start={0}
                      end={parseIndex(items[1]?.index) ?? 0}
                      delay={1 * 0.2}
                      duration={0.8}
                      separator=" "
                      enableScrollSpy
                      scrollSpyOnce
                      className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem]  leading-[60px] xl:leading-[80px]"
                    >
                      {({ countUpRef }) => (
                        <span
                          className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]"
                          ref={countUpRef}
                        />
                      )}
                    </CountUp>
                    {items[1]?.percentage && "%"}
                  </span>
                  <span className="text-neutral-900 text-m-regular leading-[28px]">
                    {items[1]?.text}
                  </span>
                </div>
                <div className="absolute top-0 right-0 w-[150px] h-[146px] flex items-center justify-center">
                  <Icon name="fig7" color="#0E8668" />
                </div>
                <div className="absolute w-[72px] h-[72px] top-[30px] right-[26px]">
                  {items[1]?.image?.url && (
                    <Image
                      fill
                      sizes="60px"
                      alt={""}
                      src={`${items[1]?.image?.url}`}
                    />
                  )}
                </div>
                <div className="absolute bottom-0 left-[calc(50%-80px)] w-[206px] h-[39px]">
                  <Icon name="fig9" />
                </div>
              </div>
            </motion.div>
          )}
          {items[3]?.index && (
            <motion.div
              variants={fadeUpVariants as any}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={3}
            >
              <div className="bg-[#FDEEE8] min-h-[400px] xl:min-h-[460px] relative flex flex-col overflow-hidden ">
                <div
                  className="flex flex-col justify-end flex-1
                              pt-[44px] pb-[44px] pl-[32px] pr-[32px]
                              xl:pt-[44px] xl:pb-[44px] xl:pl-[44px] xl:pr-[44px]
                              gap-[8px]"
                >
                  <span className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]">
                    <CountUp
                      start={0}
                      end={parseIndex(items[3]?.index) ?? 0}
                      delay={3 * 0.2}
                      duration={0.8}
                      separator=" "
                      enableScrollSpy
                      scrollSpyOnce
                      className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]"
                    >
                      {({ countUpRef }) => (
                        <span
                          className="text-neutral-900 font-bold text-[2.5rem] xl:text-[3.75rem] leading-[60px] xl:leading-[80px]"
                          ref={countUpRef}
                        />
                      )}
                    </CountUp>
                    {items[3]?.percentage && "%"}
                  </span>
                  <span className="text-neutral-900 text-m-regular leading-[28px]">
                    {items[3]?.text}
                  </span>
                </div>
                <div className="absolute top-0 right-0 w-[150px] h-[146px] flex items-center justify-center">
                  <Icon name="fig7" color="#F6B89B" />
                </div>
                <div className="absolute w-[72px] h-[72px] top-[30px] right-[26px]">
                  {items[3]?.image?.url && (
                    <Image
                      fill
                      sizes="60px"
                      alt={""}
                      src={`${items[3]?.image?.url}`}
                    />
                  )}
                </div>
                <div className="absolute bottom-0 left-[calc(50%-80px)] w-[206px] h-[39px]">
                  <Icon name="fig9" color="#FADACC" />
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};
